import React, { useState } from 'react';
import AppLayout from 'layouts/AppLayout';
import { useRole } from 'context/RoleContext';
import { ROLES } from 'config/roleConfig';
import Icon from 'components/AppIcon';
import { TEAM_MEMBERS } from './components/teamData';
import MembersTab from './components/MembersTab';
import RolesPermissionsTab from './components/RolesPermissionsTab';
import PendingInvitesTab from './components/PendingInvitesTab';
import AuditTrailTab from './components/AuditTrailTab';
import InviteMemberModal from './components/InviteMemberModal';

function Toast({ message, onClose }) {
  return (
    <div
      className="fixed top-5 right-5 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border border-border bg-card z-[9999] max-w-sm"
    >
      <Icon name="CheckCircle" size={18} color="#22c55e" />
      <span className="font-caption text-sm text-foreground">{message}</span>
      <button onClick={onClose} className="ml-auto p-0.5 rounded hover:bg-muted">
        <Icon name="X" size={14} color="var(--color-muted-foreground)" />
      </button>
    </div>
  );
}

const TABS = [
  { id: 'members', label: 'Members', icon: 'Users' },
  { id: 'roles', label: 'Roles & Permissions', icon: 'ShieldCheck' },
  { id: 'invites', label: 'Pending Invites', icon: 'MailPlus' },
  { id: 'audit', label: 'Audit Trail', icon: 'ClipboardList' },
];

export default function TeamManagementPage() {
  const { role } = useRole();
  const isAdmin = role === ROLES.PLATFORM_ADMIN;
  const [activeTab, setActiveTab] = useState('members');
  const [members, setMembers] = useState(TEAM_MEMBERS);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const stats = [
    { label: 'Total Members', value: members?.length, icon: 'Users', color: 'text-foreground', bg: 'bg-muted', iconColor: 'var(--color-muted-foreground)' },
    { label: 'Active', value: members?.filter(m => m?.status === 'Active')?.length, icon: 'UserCheck', color: 'text-green-700', bg: 'bg-green-100', iconColor: '#15803d' },
    { label: 'Pending Invite', value: members?.filter(m => m?.status === 'Pending')?.length, icon: 'Clock', color: 'text-amber-700', bg: 'bg-amber-100', iconColor: '#b45309' },
    { label: 'Deactivated', value: members?.filter(m => m?.status === 'Inactive')?.length, icon: 'UserX', color: 'text-red-700', bg: 'bg-red-100', iconColor: '#b91c1c' },
  ];

  return (
    <AppLayout>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      <div className="min-h-screen bg-background">
        {/* Sticky header */}
        <header
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground">Team Management</h1>
              <p className="font-caption text-sm text-muted-foreground mt-0.5">
                Manage members, roles, and permissions — <span className="italic">Sample data, for demonstration purposes only</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isAdmin && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary/90 transition-colors"
                >
                  <Icon name="UserPlus" size={15} />
                  Invite Member
                </button>
              )}
              <button
                onClick={() => showToast('Member list exported')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-border text-muted-foreground bg-card hover:bg-muted transition-colors"
              >
                <Icon name="Download" size={15} />
                Export
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="px-6 md:px-10 lg:px-12 py-8 space-y-6">

          {/* Read-only Banner */}
          {!isAdmin && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
              <Icon name="Info" size={16} color="#d97706" />
              <p className="font-caption text-sm text-amber-800 font-medium">You have view-only access to team management.</p>
            </div>
          )}

          {isAdmin ? (
            <>
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1 max-w-sm">
                  <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e?.target?.value)}
                    placeholder="Search members…"
                    className="w-full pl-9 pr-4 py-2 border border-border rounded-xl font-caption text-sm bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-3">
                {stats?.map(stat => (
                  <div key={stat?.label} className="flex items-center gap-2.5 px-4 py-2.5 bg-card rounded-xl border border-border">
                    <div className={`w-7 h-7 rounded-lg ${stat?.bg} flex items-center justify-center`}>
                      <Icon name={stat?.icon} size={14} color={stat?.iconColor} />
                    </div>
                    <div>
                      <p className="font-caption text-xs text-muted-foreground font-medium">{stat?.label}</p>
                      <p className={`font-data text-base font-bold ${stat?.color}`}>{stat?.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tabs */}
              <div className="flex gap-1 p-1 bg-card rounded-xl border border-border w-fit">
                {TABS?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-caption text-sm font-medium transition-all ${
                      activeTab === tab?.id
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={15} />
                    {tab?.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div>
                {activeTab === 'members' && (
                  <MembersTab
                    members={members}
                    setMembers={setMembers}
                    isAdmin={isAdmin}
                    showToast={showToast}
                    searchQuery={searchQuery}
                  />
                )}
                {activeTab === 'roles' && (
                  <RolesPermissionsTab isAdmin={isAdmin} showToast={showToast} />
                )}
                {activeTab === 'invites' && (
                  <PendingInvitesTab isAdmin={isAdmin} showToast={showToast} />
                )}
                {activeTab === 'audit' && (
                  <AuditTrailTab showToast={showToast} />
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: 'var(--color-muted)' }}
              >
                <Icon name="Lock" size={24} color="var(--color-muted-foreground)" />
              </div>
              <div>
                <p className="font-heading font-semibold text-foreground">Restricted Access</p>
                <p className="text-sm font-caption mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
                  Team member data is visible to Platform Admins only.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {showInviteModal && (
        <InviteMemberModal onClose={() => setShowInviteModal(false)} showToast={showToast} />
      )}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </AppLayout>
  );
}
